import { Formik, FormikProps } from "formik";
import { useEffect, useMemo } from "react";
import { ContractSettingsFormValues, getContractSettingsInitialValues } from "../mock-data";
import { contractSettingsValidationSchema } from "../../../../../common/validations/contract-settings.validations";

interface ContractLengthHandlers {
  handleIncrement: () => void;
  handleDecrement: () => void;
  handleChange: (value: string | number) => void;
  handleAutoRenewChange: (value: boolean) => void;
}

interface RenewalNotificationsHandlers {
  handleValueChange: (id: number, value: string | number) => void;
  handleIncrement: (id: number) => void;
  handleDecrement: (id: number) => void;
  handleCheckboxChange: (id: number) => void;
}

interface ConsumptionNotificationsHandlers {
  handleOverSubscriptionToggle: (value: boolean) => void;
  handleOverProspectToggle: (value: boolean) => void;
  handlePercentageToggle: (percentage: string) => void;
}

interface SendNotificationsHandlers {
  handleAddEmail: () => void;
  handleRemoveEmail: (id: string) => void;
  handleEmailChange: (id: string, address: string) => void;
}

interface ContractSettingsFormHandlers {
  handleContractLength: ContractLengthHandlers;
  handleRenewalNotifications: RenewalNotificationsHandlers;
  handleConsumptionNotifications: ConsumptionNotificationsHandlers;
  handleSendNotifications: SendNotificationsHandlers;
}

interface ContractSettingsFormChildrenProps extends FormikProps<ContractSettingsFormValues>, ContractSettingsFormHandlers {}

interface ContractSettingsFormProps {
  initialValues?: ContractSettingsFormValues;
  onFormStateChange?: (values: ContractSettingsFormValues, isValid: boolean) => void;
  children: (props: ContractSettingsFormChildrenProps) => React.ReactNode;
}

interface FormContentProps {
  formikProps: FormikProps<ContractSettingsFormValues>;
  onFormStateChange?: (values: ContractSettingsFormValues, isValid: boolean) => void;
  children: (props: ContractSettingsFormChildrenProps) => React.ReactNode;
}

const FormContent = ({ formikProps, onFormStateChange, children }: FormContentProps) => {
  const { values, isValid, setFieldValue } = formikProps;

  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(values, isValid);
    }
  }, [values, isValid]);

  const handleContractLength = useMemo<ContractLengthHandlers>(() => ({
    handleIncrement: () => {
      setFieldValue("contractLength", values.contractLength + 1);
    },
    handleDecrement: () => {
      setFieldValue("contractLength", Math.max(1, values.contractLength - 1));
    },
    handleChange: (value: string | number) => {
      const numValue = typeof value === 'string' ? Number.parseInt(value, 10) : value;
      if (!isNaN(numValue) && numValue > 0) {
        setFieldValue("contractLength", numValue);
      }
    },
    handleAutoRenewChange: (value: boolean) => {
      setFieldValue("autoRenew", value);
    },
  }), [values.contractLength, setFieldValue]);

  const handleRenewalNotifications = useMemo<RenewalNotificationsHandlers>(() => ({
    handleValueChange: (id: number, value: string | number) => {
      const newValue = typeof value === "string" ? Number.parseInt(value) || 0 : value;
      const updatedNotifications = values.renewalNotifications.map((item) =>
        item.id === id ? { ...item, value: Math.max(0, newValue) } : item,
      );
      setFieldValue("renewalNotifications", updatedNotifications);
    },
    handleIncrement: (id: number) => {
      const updatedNotifications = values.renewalNotifications.map((item) =>
        item.id === id ? { ...item, value: item.value + 1 } : item,
      );
      setFieldValue("renewalNotifications", updatedNotifications);
    },
    handleDecrement: (id: number) => {
      const updatedNotifications = values.renewalNotifications.map((item) =>
        item.id === id ? { ...item, value: Math.max(0, item.value - 1) } : item,
      );
      setFieldValue("renewalNotifications", updatedNotifications);
    },
    handleCheckboxChange: (id: number) => {
      const updatedNotifications = values.renewalNotifications.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item,
      );
      setFieldValue("renewalNotifications", updatedNotifications);
    },
  }), [values.renewalNotifications, setFieldValue]);

  const handleConsumptionNotifications = useMemo<ConsumptionNotificationsHandlers>(() => ({
    handleOverSubscriptionToggle: (value: boolean) => {
      setFieldValue("isOverSubscriptionEnabled", value);
    },
    handleOverProspectToggle: (value: boolean) => {
      setFieldValue("isOverProspectEnabled", value);
    },
    handlePercentageToggle: (percentage: string) => {
      const updatedPercentages = values.selectedPercentages.includes(percentage)
        ? values.selectedPercentages.filter(p => p !== percentage)
        : [...values.selectedPercentages, percentage];
      setFieldValue("selectedPercentages", updatedPercentages);
    },
  }), [values.selectedPercentages, setFieldValue]);

  const handleSendNotifications = useMemo<SendNotificationsHandlers>(() => ({
    handleAddEmail: () => {
      const updatedEmails = [
        ...values.emails,
        { id: Date.now().toString(), address: '' },
      ];
      setFieldValue("emails", updatedEmails);
    },
    handleRemoveEmail: (id: string) => {
      const updatedEmails = values.emails.filter((email) => email.id !== id);
      setFieldValue("emails", updatedEmails);
    },
    handleEmailChange: (id: string, address: string) => {
      const updatedEmails = values.emails.map((email) => 
        email.id === id ? { ...email, address } : email
      );
      setFieldValue("emails", updatedEmails);
    },
  }), [values.emails, setFieldValue]);

  return <>{children({ 
    ...formikProps, 
    handleContractLength,
    handleRenewalNotifications,
    handleConsumptionNotifications,
    handleSendNotifications,
  })}</>;
};

const ContractSettingsForm = ({
  initialValues = getContractSettingsInitialValues(),
  onFormStateChange,
  children,
}: ContractSettingsFormProps) => {
  const handleSubmit = (values: ContractSettingsFormValues) => {
    console.log("Form submitted:", values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={contractSettingsValidationSchema}
      onSubmit={handleSubmit}
      validateOnMount={true}
    >
      {(formikProps) => (
        <FormContent
          formikProps={formikProps}
          onFormStateChange={onFormStateChange}
          children={children}
        />
      )}
    </Formik>
  );
};

export default ContractSettingsForm;

